/*
 * SicBloqueOriginacion.jsx — Bloque SIC de la pantalla de Originación
 * Fase 8+ · Feb-2026
 *
 * Reemplaza al bloque roto de `Originacion.jsx` que tenía:
 *   - Botón "Generar autorización" que siempre fallaba con 400.
 *   - Botón "Consultar (simulación)" que siempre devolvía 409 en modo mock.
 *
 * Diseño autorizado por el usuario:
 *   1) BLOQUE 1 — Autorización del cliente (reusa <SicAutorizacionProspecto />
 *      con sus 3 caminos: firmó carta / autorizó por otro medio / no autoriza).
 *   2) BLOQUE 2 — Consulta:
 *      • Sub-bloque API: solo aparece habilitado si la integración con la sociedad
 *        elegida está conectada de verdad (modo=sandbox|produccion). Si no, muestra
 *        el estado con claridad. Nada de "simulación".
 *      • Sub-bloque Manual: el ejecutivo entró al portal del buró por su cuenta,
 *        aquí sube el PDF. Exige autorización vigente + sociedad + fecha + PDF.
 *
 * Estrictamente aditivo: cero cambios a `SicAutorizacionProspecto` en su comportamiento
 * previo; solo consumo del componente. Backend usado: /sic/autorizacion (existente),
 * /sic/consultar (existente), /sic/consulta-manual (nuevo, aditivo), /documentos/upload
 * (existente), /integraciones/credenciales (existente).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileSearch, Upload, Loader2, AlertCircle, CheckCircle2,
  Cable, Ban, ExternalLink, ArrowRight, Eye, History, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  SicAutorizacionProspecto, elegirAutorizacion, autorizacionVigente,
} from "@/components/common/SicAutorizacionProspecto";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// Sociedades soportadas. "Ambas" NO existe en consulta manual porque los reportes
// del portal son de UN buró específico (regla del usuario).
const SOCIEDADES = [
  { value: "Círculo de Crédito", label: "Círculo de Crédito", proveedor: "circulo" },
  { value: "Buró de Crédito", label: "Buró de Crédito (Personas Físicas)", proveedor: "buro_pf" },
  { value: "Buró Empresas / PM-PFAE", label: "Buró de Crédito Empresas (PM / PFAE)", proveedor: "buro_pm" },
];

const ACCEPT = ".pdf";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// oct-2026 · La fecha por defecto y el tope del campo tienen que ser el HOY DE CIUDAD DE
// MEXICO, que es con el que valida el servidor. Con `toISOString()` (UTC), entre las 18:00
// y la medianoche de CDMX el formulario proponia el dia siguiente y el registro se rechazaba
// con «La fecha de consulta no puede ser futura» sin que el operador tocara nada.
const hoyISO = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Mexico_City", year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date());

// Abre el PDF resguardado (cifrado) con una URL firmada de un solo uso.
async function abrirDocumento(docId) {
  if (!docId) { toast.error("Esta consulta no tiene PDF asociado."); return; }
  try {
    const { data } = await api.post(`/documentos/${docId}/signed-url`);
    window.open(`${BACKEND}${data.url}`, "_blank");
  } catch (e) { toast.error(apiError(e)); }
}

export function SicBloqueOriginacion({ solicitud, onChanged }) {
  const [sociedad, setSociedad] = useState("Círculo de Crédito");
  const [modos, setModos] = useState({});   // { proveedor: "mock"|"sandbox"|"produccion" }
  const [estados, setEstados] = useState({}); // { proveedor: "mock"|"sandbox"|"produccion"|"no configurado" }
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [loadingAuth, setLoadingAuth] = useState(true);
  // sep-2026 · HISTORIAL DE CONSULTAS. El bloque solo capturaba: al reabrir la solicitud el
  // formulario salía vacío y la consulta ya registrada (con su PDF) no se veía en ningún lado
  // de esta pantalla, aunque estaba guardada en `consultas_sic` y visible en el Perfil 360.
  const [consultas, setConsultas] = useState([]);
  const [loadingConsultas, setLoadingConsultas] = useState(true);

  const loadConsultas = useCallback(async () => {
    if (!solicitud?.cliente_id) { setConsultas([]); setLoadingConsultas(false); return; }
    setLoadingConsultas(true);
    try {
      const { data } = await api.get("/sic/consultas", { params: { cliente_id: solicitud.cliente_id } });
      setConsultas(Array.isArray(data) ? data : []);
    } catch { setConsultas([]); }
    finally { setLoadingConsultas(false); }
  }, [solicitud?.cliente_id]);

  useEffect(() => { loadConsultas(); }, [loadConsultas]);

  // Cargar autorizaciones del cliente y modos de las integraciones.
  const loadAuth = useCallback(async () => {
    if (!solicitud?.cliente_id) { setAutorizaciones([]); setLoadingAuth(false); return; }
    setLoadingAuth(true);
    try {
      const { data } = await api.get("/sic/autorizaciones", { params: { cliente_id: solicitud.cliente_id } });
      setAutorizaciones(Array.isArray(data) ? data : []);
    } catch { setAutorizaciones([]); }
    finally { setLoadingAuth(false); }
  }, [solicitud?.cliente_id]);

  useEffect(() => { loadAuth(); }, [loadAuth]);

  useEffect(() => {
    api.get("/integraciones/credenciales").then((r) => {
      const m = {}; const e = {};
      (r.data || []).forEach((c) => { m[c.proveedor] = c.modo; e[c.proveedor] = c.estado; });
      setModos(m); setEstados(e);
    }).catch(() => {});
  }, []);

  // sep-2026 · FUENTE ÚNICA: la misma autorización que se pasa al formulario (prop) es la
  // que alimenta el badge del encabezado y el candado de la consulta. Antes el encabezado
  // leía `/sic/autorizaciones` y el formulario no leía nada, así que la pantalla se
  // contradecía (badge "Vigente hasta …" + formulario pidiendo la carta otra vez).
  const authElegida = useMemo(() => elegirAutorizacion(autorizaciones), [autorizaciones]);
  const authVigente = useMemo(
    () => (autorizacionVigente(authElegida) ? authElegida : null), [authElegida]);

  const socMeta = SOCIEDADES.find((s) => s.value === sociedad) || SOCIEDADES[0];
  const modoSoc = modos[socMeta.proveedor] || "mock";
  const estadoSoc = estados[socMeta.proveedor] || "no configurado";
  const apiConectada = estadoSoc === "sandbox" || estadoSoc === "produccion";

  const handleAutorizacionCreada = () => {
    loadAuth();
    onChanged && onChanged();
  };
  const handleConsultaGuardada = () => {
    loadConsultas();
    onChanged && onChanged();
  };

  return (
    <div className="rounded-lg border border-sage/40 bg-sage/15/50 p-4" data-testid="sic-bloque-originacion">
      <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
        <ShieldCheck className="h-4 w-4 text-forest-600" /> Consulta SIC (Círculo / Buró)
      </h4>

      {/* Estado de la autorización — resumen visible siempre */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Autorización del cliente:</span>
        {loadingAuth ? (
          <span className="inline-flex items-center gap-1 text-slate-400" data-testid="sic-auth-loading">
            <Loader2 className="h-3 w-3 animate-spin" /> Cargando…
          </span>
        ) : authVigente ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800" data-testid="sic-auth-vigente">
            <CheckCircle2 className="h-3 w-3" /> Vigente hasta {authVigente.vigencia || "—"} · {authVigente.medio}
          </span>
        ) : authElegida?.vigencia && authElegida.decision !== "no_autoriza" ? (
          /* oct-2026 · existe autorización pero YA VENCIÓ: decirlo con su fecha, no un
             genérico «Sin autorización vigente» que deja al operador sin saber qué hacer. */
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid="sic-auth-vencida">
            <AlertCircle className="h-3 w-3" /> Venció el {authElegida.vigencia} · {authElegida.medio || "—"} — renuévala para consultar
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid="sic-auth-ninguna">
            <AlertCircle className="h-3 w-3" /> Sin autorización vigente
          </span>
        )}
      </div>

      {/* BLOQUE 1 — Autorización del cliente */}
      {!solicitud?.cliente_id ? (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-500"
           data-testid="sic-bloque-sin-cliente">
          Liga primero un cliente a la solicitud para registrar la autorización de consulta a Buró/Círculo.
        </p>
      ) : (
        <div className="mb-4" data-testid="sic-bloque-autorizacion">
          {loadingAuth ? (
            <p className="flex items-center gap-1.5 rounded-md border border-slate-200 p-3 text-[11px] text-slate-500"
               data-testid="sic-bloque-auth-cargando">
              <Loader2 className="h-3 w-3 animate-spin" /> Consultando la autorización resguardada…
            </p>
          ) : (
            <SicAutorizacionProspecto
              key={`sic-auth-${solicitud.cliente_id}`}
              idField="cliente_id"
              recordId={solicitud.cliente_id}
              sujeto="cliente"
              showConsultar={false}
              autorizacion={authElegida}
              onAuth={handleAutorizacionCreada}
            />
          )}
        </div>
      )}

      {/* BLOQUE 2 — Consulta */}
      {solicitud?.cliente_id && (
        <div className="space-y-3" data-testid="sic-bloque-consulta">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-slate-200" />
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Registrar consulta al buró</p>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <div>
            <Label className="mb-1 block text-xs">Sociedad</Label>
            <Select value={sociedad} onValueChange={setSociedad}>
              <SelectTrigger className="h-9 text-sm" data-testid="sic-bloque-sociedad"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOCIEDADES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Sub-bloque API */}
          <ConsultaApiBlock
            sociedad={sociedad}
            apiConectada={apiConectada}
            modoSoc={modoSoc}
            authVigente={authVigente}
            solicitud={solicitud}
            onConsultaGuardada={handleConsultaGuardada}
          />

          {/* Sub-bloque Manual — lleva pegado el historial de lo ya registrado */}
          <ConsultaManualBlock
            sociedad={sociedad}
            authVigente={authVigente}
            authElegida={authElegida}
            solicitud={solicitud}
            consultas={consultas}
            loadingConsultas={loadingConsultas}
            onConsultaGuardada={handleConsultaGuardada}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Historial — consultas YA registradas del titular (mismo patrón visual que el
// bloque "Ya cargado" de la autorización). Es lo que faltaba: la pantalla solo
// capturaba, así que al reabrir la solicitud parecía que nada se había guardado.
//
// oct-2026 · Vive PEGADO al bloque de consulta manual (antes estaba suelto arriba, lejos
// del formulario) y lleva el botón que vuelve a abrir el formulario.
// ─────────────────────────────────────────────────────────────────────────────
function ConsultasRegistradas({ loading, consultas, onAbrir, mostrarAbrir }) {
  if (loading) {
    return (
      <p className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white p-3 text-[11px] text-slate-500"
         data-testid="sic-consultas-cargando">
        <Loader2 className="h-3 w-3 animate-spin" /> Consultando las consultas ya registradas…
      </p>
    );
  }
  if (!consultas.length) {
    return (
      <p className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2.5 text-[11px] text-slate-500"
         data-testid="sic-consultas-vacio">
        Aún no hay consultas a Círculo/Buró registradas para este cliente.
      </p>
    );
  }
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="sic-consultas-registradas">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-800">
          <History className="h-3.5 w-3.5" /> Consultas registradas ({consultas.length})
        </div>
        {mostrarAbrir && (
          <Button
            type="button" size="sm" variant="outline"
            data-testid="sic-manual-abrir-btn"
            onClick={onAbrir}
            className="h-7 gap-1.5 border-emerald-300 bg-white px-2.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            <Plus className="h-3.5 w-3.5" /> Registrar nueva consulta
          </Button>
        )}
      </div>
      <ul className="space-y-1.5">
        {consultas.map((q) => (
          <li key={q.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-emerald-900"
              data-testid={`sic-consulta-registrada-${q.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium">Ya cargado{q.fecha ? ` (${q.fecha})` : ""}</span>
            <span>Sociedad: <b>{q.sociedad || "—"}</b></span>
            <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
              q.modo === "Manual" ? "border-sky-200 bg-sky-100 text-sky-800"
                : "border-emerald-300 bg-white text-emerald-700"}`}
              data-testid={`sic-consulta-origen-${q.id}`}>
              {q.modo === "Manual" ? "Portal (manual)" : `API · ${q.modo || "—"}`}
            </span>
            {q.score != null && <span>Score <b>{q.score}</b></span>}
            <span className="font-mono text-[10px] text-emerald-700">{q.folio}</span>
            {q.usuario && <span>Registrada por <b>{q.usuario}</b></span>}
            {q.documento_id && (
              <Button type="button" size="sm" variant="ghost"
                className="h-5 gap-1 px-1.5 text-[11px] text-emerald-700 hover:text-emerald-800"
                data-testid={`sic-consulta-ver-${q.id}`}
                onClick={() => abrirDocumento(q.documento_id)}>
                <Eye className="h-3 w-3" /> Ver documento
              </Button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-emerald-700">
        Cada consulta es un evento y queda en el historial. Subir el mismo PDF otra vez no la duplica.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-bloque API — solo habilitado si la integración está REALMENTE conectada.
// ─────────────────────────────────────────────────────────────────────────────
function ConsultaApiBlock({ sociedad, apiConectada, modoSoc, authVigente, solicitud, onConsultaGuardada }) {
  const [busy, setBusy] = useState(false);

  const consultar = async () => {
    if (!authVigente) { toast.error("No hay autorización vigente del cliente."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sic/consultar", {
        cliente_id: solicitud.cliente_id, solicitud_id: solicitud.id,
        sociedad, producto: solicitud.producto || "Crédito simple",
      });
      toast.success(`Consulta ${sociedad} completada · Score ${data.consulta?.score ?? "—"} · ${data.alertas} alerta(s).`);
      onConsultaGuardada && onConsultaGuardada();
    } catch (e) {
      toast.error(apiError(e), { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="sic-bloque-api">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
        <Cable className="h-3.5 w-3.5 text-primary" /> Consulta por API
      </div>
      {apiConectada ? (
        <>
          <p className="mb-2 text-[11px] text-emerald-700" data-testid="sic-api-conectada">
            Integración con <b>{sociedad}</b> conectada ({modoSoc}). La consulta se ejecuta contra el buró
            y el reporte se resguarda cifrado.
          </p>
          <Button
            size="sm"
            data-testid="sic-api-consultar-btn"
            onClick={consultar}
            disabled={busy || !authVigente}
            className="bg-accent hover:bg-forest-700"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSearch className="mr-1.5 h-4 w-4" />}
            Consultar {sociedad} por API
          </Button>
        </>
      ) : (
        <div className="flex items-start gap-2 rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-700" data-testid="sic-api-no-conectada">
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
          <div>
            <p>
              <b>Consulta por API no disponible.</b> No hay integración configurada con <b>{sociedad}</b>.
            </p>
            <Link to="/centro-integraciones" className="mt-1 inline-flex items-center gap-1 text-primary hover:underline" data-testid="sic-api-cfg-link">
              Configurar en Centro de Integraciones <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-bloque Manual — subir el PDF del portal del buró.
// ─────────────────────────────────────────────────────────────────────────────
function ConsultaManualBlock({ sociedad, authVigente, authElegida, solicitud,
                               consultas, loadingConsultas, onConsultaGuardada }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  // oct-2026 · El rechazo del backend se PINTA en el bloque y se queda ahí hasta el
  // siguiente intento. Antes solo salía un toast: se desvanecía en 9 s y el operador
  // veía el formulario vacío sin consulta en el historial, sin saber por qué.
  const [error, setError] = useState("");
  // oct-2026 · El formulario se ABRE y se CIERRA. Antes quedaba siempre abierto y vacío
  // después de registrar, así que el operador creía que no se había guardado: capturaba
  // dos veces. `null` = nadie lo ha tocado todavía; en ese caso solo se abre si el cliente
  // aún no tiene ninguna consulta registrada.
  const [abiertoManual, setAbiertoManual] = useState(null);
  const abierto = abiertoManual ?? (!loadingConsultas && consultas.length === 0);

  const puede = !!authVigente && !!file && !!fecha;

  const guardar = async () => {
    if (!authVigente) { toast.error("No hay autorización vigente del cliente."); return; }
    if (!file) { toast.error("Sube el PDF del reporte del buró."); return; }
    if (!fecha) { toast.error("Captura la fecha en la que consultaste el portal."); return; }
    setBusy(true); setError("");
    try {
      // 1) Subir PDF cifrado. Se marca inicialmente con tipo genérico; el endpoint
      //    /sic/consulta-manual re-etiqueta clasificacion="Reporte SIC" al guardar.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", `Reporte SIC (portal) · ${sociedad}`);
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Reporte SIC");
      fd.append("cliente_id", solicitud.cliente_id);
      if (solicitud.id) fd.append("referencia_id", solicitud.id);
      const doc = await api.post("/documentos/upload", fd).then((r) => r.data);

      // 2) Registrar la consulta manual.
      const res = await api.post("/sic/consulta-manual", {
        cliente_id: solicitud.cliente_id,
        solicitud_id: solicitud.id || null,
        sociedad,
        fecha_consulta: fecha,
        documento_id: doc.id,
      }).then((r) => r.data);
      if (res?.duplicado_evitado) {
        toast.info(res.mensaje || "Este mismo PDF ya estaba registrado. No se duplicó.", { duration: 9000 });
      } else {
        toast.success(
          `Consulta registrada · ${sociedad} · ${fecha}. El PDF quedó resguardado en el expediente.`,
          { duration: 7000 });
      }
      // Se cierra el formulario: la prueba de que sí se guardó es el registro de arriba.
      setFile(null); setFecha(hoyISO()); setAbiertoManual(false);
      onConsultaGuardada && onConsultaGuardada();
    } catch (e) {
      // El archivo elegido NO se pierde: el operador corrige y vuelve a intentar.
      setError(apiError(e));
      toast.error(apiError(e), { duration: 9000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="sic-bloque-manual">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
        <Upload className="h-3.5 w-3.5 text-primary" /> Consulta manual (subir PDF del portal)
      </div>

      {/* Lo ya registrado, PEGADO a esta sección: es la prueba de que se guardó, justo
          donde el operador acaba de capturar. */}
      <ConsultasRegistradas
        loading={loadingConsultas}
        consultas={consultas}
        mostrarAbrir={!abierto}
        onAbrir={() => { setError(""); setAbiertoManual(true); }}
      />

      {abierto && (
        <div className="mt-3" data-testid="sic-manual-formulario">
          <p className="mb-2 text-[11px] text-slate-500">
            Entra al portal de <b>{sociedad}</b> por tu cuenta, obtén el reporte y sube el PDF aquí.
            El archivo queda cifrado en el expediente y aparece en el Perfil 360 del cliente.
          </p>

          {!authVigente && (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800"
                 data-testid="sic-manual-sin-auth">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {authElegida?.vigencia && authElegida.decision !== "no_autoriza" ? (
                <span>
                  La autorización del cliente <b>venció el {authElegida.vigencia}</b>
                  {authElegida.fecha ? ` (registrada el ${authElegida.fecha})` : ""}. No se puede
                  registrar una consulta manual con una autorización vencida: usa <b>Reemplazar
                  documento</b> en el bloque de arriba para cargar la carta nueva y vuelve aquí.
                </span>
              ) : authElegida?.decision === "no_autoriza" ? (
                <span>
                  El cliente <b>NO autorizó</b> la consulta a buró ({authElegida.fecha || "—"}).
                  No se puede registrar una consulta manual mientras exista esa negativa.
                </span>
              ) : (
                <span>
                  Sin autorización vigente del cliente no se puede registrar una consulta manual.
                  Captura la autorización arriba antes de continuar.
                </span>
              )}
            </div>
          )}

          {/* oct-2026 · Rechazo del servidor VISIBLE y persistente (ya no solo un toast). */}
          {error && (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-2.5 text-[11px] text-rose-800"
                 data-testid="sic-manual-error">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <p className="font-semibold">La consulta NO se registró.</p>
                <p className="mt-0.5">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[11px] text-slate-600">Fecha de consulta</Label>
              <Input
                type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
                max={hoyISO()}
                data-testid="sic-manual-fecha"
                disabled={busy || !authVigente}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600">PDF del reporte</Label>
              <Input
                type="file" accept={ACCEPT}
                data-testid="sic-manual-file"
                disabled={busy || !authVigente}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm"
              />
              {file && <p className="mt-1 text-[10px] text-slate-500">Archivo: {file.name}</p>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              data-testid="sic-manual-guardar-btn"
              onClick={guardar}
              disabled={busy || !puede}
              className="bg-primary hover:bg-sidebar-hover"
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
              Registrar consulta manual
            </Button>
            {consultas.length > 0 && (
              <Button
                type="button" size="sm" variant="ghost"
                data-testid="sic-manual-cancelar-btn"
                disabled={busy}
                onClick={() => { setFile(null); setError(""); setAbiertoManual(false); }}
                className="h-8 px-2 text-[11px] text-slate-500 hover:text-slate-700"
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SicBloqueOriginacion;
